import clsx from 'clsx'
import type {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  MouseEventHandler,
} from 'react'
import React, { createContext, useState, useContext } from 'react'
import type { TabColor, TabSize } from './Tab'

interface GroupProps {
  color?: TabColor
  defaultTab: string
  disabled?: boolean
  onClick?: MouseEventHandler<HTMLButtonElement>
  orientation?: Orientation
  size?: TabSize
  adornmentPosition?: Orientation
}

type GroupState = {
  color: TabColor
  disabled?: boolean | undefined
  onClick?: MouseEventHandler<HTMLButtonElement>
  selectedTab: string
  setSelectedTab: Dispatch<SetStateAction<string>>
  size: TabSize
  adornmentPosition: Orientation
}

export type Orientation = 'vertical' | 'horizontal'

export const GroupContext = createContext<GroupState | null>(null)

export const useGroup = (): GroupState => useContext(GroupContext) as GroupState

function Group({
  children,
  color = 'default',
  defaultTab,
  disabled,
  onClick,
  orientation = 'horizontal',
  size = 'large',
  adornmentPosition = 'vertical',
}: PropsWithChildren<GroupProps>): JSX.Element {
  const [selectedTab, setSelectedTab] = useState(defaultTab)

  return (
    <div
      className={clsx(
        'flex w-full flex-wrap',
        orientation === 'vertical' ? 'flex-col' : null,
      )}
      role="tablist"
    >
      <GroupContext.Provider
        value={{
          color,
          disabled,
          onClick,
          selectedTab,
          setSelectedTab,
          size,
          adornmentPosition,
        }}
      >
        {children}
      </GroupContext.Provider>
    </div>
  )
}

export default Group
